import {
  Alert,
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { Dispatch, FC, SetStateAction, useState } from "react";
import { Gender, PatientDetails } from "../types";
import { DateTime } from "luxon";
import * as apiClient from "../api";
import { Email, Phone, Save } from "@mui/icons-material";
import { isAxiosError } from "axios";

interface PatientDetailsTabProps {
  patientId: string;
  patientDetails: PatientDetails;
  setPatient: Dispatch<SetStateAction<PatientDetails>>;
}

const PatientDetailsTab: FC<PatientDetailsTabProps> = ({
  patientId,
  patientDetails,
  setPatient,
}) => {
  const defaultErrorMessage = "Could not update details";
  const [showSaving, setShowSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(defaultErrorMessage);
  const [firstName, setFirstName] = useState(patientDetails.firstName);
  const [lastName, setLastName] = useState(patientDetails.lastName);
  const [email, setEmail] = useState(patientDetails.email);
  const [gender, setGender] = useState(patientDetails.gender);
  const [dob, setDob] = useState<DateTime | null>(
    DateTime.fromISO(patientDetails.dob),
  );
  const [phone, setPhone] = useState(patientDetails.phone);

  const isValid =
    !!firstName && !!lastName && !!email && !!gender && !!dob && !!phone;

  const newPatientDetails = {
    firstName,
    lastName,
    email,
    gender,
    dob: dob?.toFormat("dd/MM/yyyy")!,
    phone,
  };

  const edited =
    patientDetails.firstName !== newPatientDetails.firstName ||
    patientDetails.lastName !== newPatientDetails.lastName ||
    patientDetails.email !== newPatientDetails.email ||
    patientDetails.gender !== newPatientDetails.gender ||
    patientDetails.dob !== dob?.toFormat("yyyy-MM-dd") ||
    patientDetails.phone !== newPatientDetails.phone;

  const handleSaveClick = async () => {
    setShowSaving(true);

    try {
      await apiClient.updatePatient(patientId, newPatientDetails);
      setShowError(false);
      setShowSaved(true);
      setPatient({ ...newPatientDetails, dob: dob?.toFormat("yyyy-MM-dd")! });
    } catch (e) {
      if (isAxiosError(e)) {
        setShowSaved(false);
        setErrorMsg(e?.response?.data?.message ?? defaultErrorMessage);
        setShowError(true);
      }
    } finally {
      setShowSaving(false);
    }
  };

  const handleCloseSavedAlert = () => {
    setShowSaved(false);
  };

  const handleCloseErrorAlert = () => {
    setShowError(false);
  };

  return (
    <Stack spacing={4}>
      {showSaved && (
        <Alert severity="success" onClose={handleCloseSavedAlert}>
          Successfully updated patient details
        </Alert>
      )}
      {showError && (
        <Alert severity="error" onClose={handleCloseErrorAlert}>
          {errorMsg}
        </Alert>
      )}
      <Stack direction="row" spacing={8}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h6">General</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <TextField
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Stack>
          <FormControl>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DatePicker
                label="Date of birth"
                value={dob}
                onChange={(newDob) => setDob(newDob)}
              />
              <FormHelperText>
                {dob ? `${calculateAge(dob)} years old` : "N/A"}
              </FormHelperText>
            </LocalizationProvider>
          </FormControl>
          <FormControl>
            <InputLabel id="patient-gender-label">Gender</InputLabel>
            <Select
              labelId="patient-gender-label"
              label="Gender"
              value={gender}
              onChange={(e) => setGender(e.target.value as Gender)}
            >
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Stack spacing={2}>
          <Typography variant="h6">Contact</Typography>
          <TextField
            label="Email"
            value={email}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email />
                </InputAdornment>
              ),
            }}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            label="Phone"
            value={phone}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone />
                </InputAdornment>
              ),
            }}
            onChange={(e) => setPhone(e.target.value)}
          />
        </Stack>
      </Stack>
      <Box>
        <LoadingButton
          loading={showSaving}
          disabled={!isValid || !edited}
          loadingPosition="start"
          startIcon={<Save />}
          variant="contained"
          onClick={handleSaveClick}
        >
          Save
        </LoadingButton>
      </Box>
    </Stack>
  );
};

export const calculateAge = (dob: DateTime) => {
  const today = DateTime.now();
  const yearsExactly = today.diff(dob, "years").years;
  return Math.floor(yearsExactly);
};

export default PatientDetailsTab;
